import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "@/lib/api";

/**
 * Monthly dividend income bar chart — 12 (or 24) rolling months.
 *
 * Uses each held ticker's actual yfinance ex-div history × the *current* share
 * count for that ticker in the currently scoped portfolio. That's a "what
 * would that month have looked like at today's allocation" approximation
 * (footnoted in the card copy) — perfect for spotting seasonality but not
 * a true dollar-weighted historical income series.
 */
const RANGES = [
  { label: "12M", months: 12 },
  { label: "24M", months: 24 },
];

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmt$ = (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function TooltipBody({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const parts = Object.entries(p.by_ticker || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 shadow-sm text-xs">
      <div className="mono uppercase tracking-widest text-[10px] text-muted-foreground">{label}</div>
      <div className="serif text-lg text-primary mt-0.5">${Number(p.income).toFixed(2)}</div>
      {parts.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {parts.map(([t, v]) => (
            <div key={t} className="flex items-center justify-between gap-3">
              <span className="mono">{t}</span>
              <span className="mono text-muted-foreground">${v.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MonthlyIncomeChart({ portfolioId }) {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = { months };
        if (portfolioId) params.portfolio_id = portfolioId;
        const r = await api.get("/holdings/monthly-income", { params });
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [months, portfolioId]);

  const chartData = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows.map((r) => {
      const [y, m] = r.month.split("-");
      return { ...r, label: `${MONTH_ABBR[Number(m) - 1]} ${y.slice(2)}` };
    });
  }, [data]);

  const maxIncome = Math.max(1, ...chartData.map((r) => r.income));

  return (
    <Card data-testid="monthly-income-chart">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              <h3 className="serif text-xl">Monthly dividend income</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Actual ex-div history × your current share count — see the quarterly rhythm at a glance.
            </p>
          </div>
          <div className="inline-flex gap-1 rounded-full border border-border p-0.5" role="tablist">
            {RANGES.map((r) => (
              <button
                key={r.months}
                onClick={() => setMonths(r.months)}
                data-testid={`income-range-${r.months}`}
                className={`px-2.5 py-0.5 rounded-full mono text-[10px] uppercase tracking-widest transition-colors ${
                  months === r.months ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="h-44 grid place-items-center text-sm text-muted-foreground">Loading…</div>}

        {!loading && data && (
          <>
            <div className="mb-3 grid grid-cols-3 gap-3">
              <StatChip label="Total" value={fmt$(data.total)} testId="income-total" />
              <StatChip label="Avg / mo" value={fmt$(data.avg_monthly)} testId="income-avg" />
              <StatChip label="Paying months" value={`${data.non_zero_months} / ${data.months}`} testId="income-months" />
            </div>

            {maxIncome <= 0 ? (
              <div className="h-32 grid place-items-center text-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
                No dividend payments recorded across your holdings in the last {data.months} months.
              </div>
            ) : (
              <div className="h-44 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      axisLine={false} tickLine={false}
                      interval={months > 12 ? 1 : 0}
                    />
                    <YAxis
                      tickFormatter={(v) => `$${Math.round(v)}`}
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      axisLine={false} tickLine={false}
                      width={40}
                    />
                    <Tooltip
                      cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                      content={<TooltipBody />}
                    />
                    <Bar
                      dataKey="income"
                      fill="hsl(var(--primary))"
                      radius={[3, 3, 0, 0]}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatChip({ label, value, testId }) {
  return (
    <div className="rounded-md border border-border p-2.5" data-testid={testId}>
      <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="serif text-lg mt-0.5">{value}</div>
    </div>
  );
}
